import Vue from "vue";
import { MutationTree } from "vuex";
import { GuardStatus, Mobile } from "./types";
import { DispatchRequest } from "@/store/mobile/types";
import { AlarmQueueFilterID, AlarmQueueFilter } from "@/store/eventqueue/types";

import { getDefaultState } from "./state";

import {
	ILatLng
} from "@sureview/v2-mapping";
import {
	Guard, PatrolZone, DispatchableEvent
} from "@sureview/v2-dispatch";

import "@/scripts/dictionary";

export const mutations: MutationTree<Mobile> = {
	// Called by async actions
	setDispatchRequests(state, dispatchRequests: DispatchRequest[]) {
		state.DispatchRequests = dispatchRequests;
	},
	setGuardId(state, guardId) {
		state.DispatchGuard.guardId = guardId;
	},
	claimDispatchRequest(state, dispatchQueueId) {
		const index = state.DispatchRequests.findIndex(
			element => element.dispatchQueueID === dispatchQueueId
		);
		Vue.set(state.DispatchRequests[index], "claimed", true);
	},
	unclaimDispatchRequest(state, dispatchQueueId) {
		const index = state.DispatchRequests.findIndex(
			element => element.dispatchQueueID === dispatchQueueId
		);
		Vue.set(state.DispatchRequests[index], "claimed", false);
	},
	activateDispatchRequest(state, dispatchQueueId) {
		// Set all dispatch requests to not-active (will be overwritten on the next load
		// of 5 second intervals - but required for UX to remain responsive)
		state.DispatchRequests.forEach(function (element) {
			element.active = false;
		});
		const currentDispatchRequestIndex = state.DispatchRequests.findIndex(
			element => element.dispatchQueueID === dispatchQueueId
		);
		Vue.set(
			state.DispatchRequests[currentDispatchRequestIndex],
			"active",
			true
		);
	},
	inactivateDispatchRequest(state, dispatchQueueId) {
		const currentDispatchRequestIndex = state.DispatchRequests.findIndex(
			element => element.dispatchQueueID === dispatchQueueId
		);
		Vue.set(
			state.DispatchRequests[currentDispatchRequestIndex],
			"active",
			false
		);
	},
	setAuditEndpoint(state, auditEndpoint) {
		state.AuditEndpoint = auditEndpoint;
	},
	setUnavailableReason(state, unavailableReason) {
		state.DispatchGuard.unavailableReason = unavailableReason;
	},
	setUnavailableStatus(state, unavailableStatusId) {
		state.DispatchGuard.unavailableStatusId = unavailableStatusId;
	},
	setNotesForEvent(state, notes) {
		state.DispatchNotesForEvent = notes;
	},
	setCallSigns(state, callSigns) {
		state.CallSigns = callSigns;
	},
	setCoverCallSigns(state, callSigns) {
		state.CoverCallSigns = callSigns;
	},
	setRequestCover(state, requested: boolean) {
		state.CoverRequested = requested;
	},
	setCoverRequests(state, requestGuard?: Guard){
		state.CoverRequest = requestGuard;
		state.LeftSidebar.HasCoverRequest = !!requestGuard;
	},
	setUnavailablePickerShown(state, shown: boolean) {
		state.LeftSidebar.UnavailablePickerShown = shown;
	},
	setCoverPickerShown(state, shown: boolean) {
		state.LeftSidebar.CoverPickerShown = shown;
	},
	setMobileGuardsForEvent(state, guards) {
		state.MobileGuardsForEvent = guards;
	},
	updateMobileGuardStatus(state, { guardId, status } : { guardId: number, status: number }) {
		let guard = state.MobileGuardsForEvent.find(x => x.guardID == guardId);
		if (guard) guard.status = status;
	},
	setGuardUnavailableStatuses(state, unavailableStatuses) {
		state.UnavailableStatuses = unavailableStatuses;
	},
	setGuardStatus(state, status) {
		state.DispatchGuard.status = status;
	},
	setGuardStatusEnum(state, status: GuardStatus | null) {
		state.DispatchGuard.statusEnum = status;
	},
	setCallSign(state, callSign: string | null) {
		if (callSign == null) {
			callSign = ""
		}
		state.DispatchGuard.callSign = callSign.toLocaleUpperCase();
	},
	setOriginalCallSign(state, originalCallSign: string | null) {
		if (originalCallSign == null) {
			originalCallSign = ""
		}
		state.DispatchGuard.originalCallSign = originalCallSign.toLocaleUpperCase();
	},
	setOnDuty(state, onDuty) {
		state.DispatchGuard.onDuty = onDuty;
	},
	setCoverAvailability(state, coverAvailability) {
		state.DispatchGuard.isCoverAvailable = coverAvailability;
	},
	setDeviceLocation(state, deviceLocation) {
		state.DeviceLocation = deviceLocation;
	},
	setIsWatchingLocation(state, value: boolean) {
		state.IsWatchingLocation = value;
	},
	setFocalPoint(state, positionToFocus) {
		state.FocalPoint = positionToFocus;
	},
	toggleRightSidebarVisibility(state) {
		state.DispatchGuard.isRightSideBarOpen = !state.DispatchGuard
			.isRightSideBarOpen;
	},
	toggleLeftSidebarVisibility(state) {
		state.DispatchGuard.isLeftSideBarOpen = !state.DispatchGuard
			.isLeftSideBarOpen;
	},
	setProximityCallSigns(state, callSigns) {
		state.ProximityCallSigns = callSigns;
	},
	setShowDirections(state, showDirections) {
		state.MobileUser.showDirections = showDirections;
	},
	setHasArrived(state, hasArrived) {
		state.DispatchGuard.hasArrived = hasArrived;
	},
	resetState(state) {
		Object.assign(state, getDefaultState());
	},
	setLiveDevice(state, device) {
		state.LiveDevice = device;
	},
	setAlmostArrived(state, almostArrived) {
		if (!almostArrived) {
			state.DispatchGuard.hasArrived = almostArrived;
		}
		state.DispatchGuard.almostArrived = almostArrived;
	},
	setMobileMode(state, mode: string) {
		if (state.MobileMode !== mode) {
			// Reset the entire state, load fresh when changing mode
			Object.assign(state, getDefaultState());
			state.MobileMode = mode;
		}

		switch (state.MobileMode) {
			case "dispatch":
				state.MainComponentName = "dispatch-core";
				state.RightComponentName = "dispatch-request";
				break;
			case "live":
				state.MainComponentName = "camera-player";
				state.RightComponentName = "live-cameras";
				break;
			case "views":
				state.MainComponentName = "mobile-views";
				state.RightComponentName = "";
				break;
			case "manage":
				state.MainComponentName = "dispatch-manage-map";
				state.RightComponentName = "mobile-guards-list";
				break;
			case "events":
				state.MainComponentName = "mobile-event-map";
				state.RightComponentName = "mobile-event-queue";
				break;
			default:
				state.MainComponentName = "";
				state.RightComponentName = "";
		}
	},
	setMapEnabled(state, enabled: boolean) {
		state.MapEnabled = enabled;
	},
	setStarredDevices(state, devices) {
		state.StarredDevices = devices;
	},
	setNotificationText(state, notificationText) {
		state.NotificationText = notificationText;
	},
	setManageDataLoading: (state, loading: boolean) => state.ManageDataLoading = loading,
	setManageGuards: (state, guards: Guard[]) => state.ManageGuards = guards,
	setManagePatrolZones: (state, patrolZones: PatrolZone[]) => state.ManagePatrolZones = patrolZones,
	setManageEvents: (state, events: DispatchableEvent[]) => {
		state.ManageEvents = events;
	},
	setManageFilters: (state, filters: AlarmQueueFilter[]) => state.ManageAlarmQueueFilters = filters,
	setManageCurrentFilters: (state, filters: AlarmQueueFilter[]) => state.ManageCurrentAlarmQueueFilters = filters,
	setMapGoto: (state, gotoFunction: (latLng: ILatLng) => void) => state.MapGoto = gotoFunction,
	setQuickCloseList(state, quickCloseList) {
		state.QuickCloseList = quickCloseList;
	},
	setCurrentTour(state, currentTour: any /* FIXME ADD TYPE GuardTour*/) {
		state.CurrentTour = currentTour;
	},
	setPatrolZones(state, data: PatrolZone[]) {
		state.PatrolZones = data;
	},
	setManualLocationUpdate(state, flag: boolean) {
		state.MobileUser.manualLocationUpdate = flag;
	},
	setCurrentTourStepIdentifier(state, identifier: string) {
		state.DispatchGuard.currentTourStepIdentifier = identifier;
	},
	setUserSharedEventIds(state, eventIds: number[]) {
		state.UserSharedEventIds = eventIds;
	}
};
